import { useEffect, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, XCircle, MessageCircle, Send, Plus,
  Edit3, Trash2, FileText, ExternalLink, Save, X,
} from "lucide-react";

const SUB_TABS = [
  { k: "applications", l: "Applications" },
  { k: "partners", l: "Partners" },
  { k: "offers", l: "Offers" },
  { k: "agreements", l: "Agreements" },
  { k: "templates", l: "Templates" },
  { k: "assignments", l: "Assignments" },
  { k: "requests", l: "Requests" },
  { k: "tickets", l: "Tickets" },
  { k: "sales", l: "Manual Sales" },
  { k: "commissions", l: "Commissions" },
  { k: "payouts", l: "Payouts" },
];

export default function AdminPartnerProgram() {
  const [sub, setSub] = useState("applications");
  return (
    <div className="space-y-5" data-testid="admin-partner-program">
      <div>
        <h2 className="font-display text-2xl text-[#F4F1EA] mb-1">Partner Program</h2>
        <p className="font-body text-[12px] text-[#8a857d]">Applications · Offers · Agreements · Templates · Assignments · Requests · Tickets · Manual Sales</p>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-white/10 mb-4 overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.k} onClick={() => setSub(t.k)} data-testid={`pp-${t.k}-tab`}
            className={`px-3 py-2 font-ui text-[10px] tracking-[0.22em] uppercase border-b-2 transition-colors ${sub === t.k ? "border-[#C9A055] text-[#C9A055]" : "border-transparent text-[#8a857d] hover:text-[#F4F1EA]"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {sub === "applications" && <Applications />}
      {sub === "partners" && <Partners />}
      {sub === "offers" && <Offers />}
      {sub === "agreements" && <Agreements />}
      {sub === "templates" && <Templates />}
      {sub === "assignments" && <Assignments />}
      {sub === "requests" && <Requests />}
      {sub === "tickets" && <Tickets />}
      {sub === "sales" && <ManualSales />}
      {sub === "commissions" && <CommissionsAdmin />}
      {sub === "payouts" && <PayoutsAdmin />}
    </div>
  );
}

// -------------- helpers --------------
const Spinner = () => <div className="py-10 text-center text-[#8a857d]"><Loader2 className="animate-spin inline-block" size={16} /></div>;

function useList(url) {
  const [rows, setRows] = useState(null);
  const reload = async () => { const { data } = await http.get(url); setRows(data || []); };
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [rows, reload];
}

function Modal({ title, onClose, children, wide, testid }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()}
        className={`bg-[#0a0a0a] border border-white/10 my-12 w-full ${wide ? "max-w-3xl" : "max-w-lg"} p-5`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl text-[#F4F1EA]">{title}</h3>
          <button onClick={onClose} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// APPLICATIONS
// ============================================================================
function Applications() {
  const [rows, reload] = useList("/admin/partner/applications");
  const [open, setOpen] = useState(null);
  if (rows === null) return <Spinner />;
  return (
    <div className="space-y-3">
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr><th className="p-3 text-left">Business</th><th className="p-3 text-left">Contact</th><th className="p-3 text-left">Pieces</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">When</th><th className="p-3 text-right">Action</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-[#8a857d] italic">No applications yet.</td></tr> :
              rows.map(a => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`pp-app-${a.id}`}>
                  <td className="p-3 text-[#F2EFE8]">{a.business_name}<div className="text-[11px] text-[#8a857d]">{a.city}{a.state ? `, ${a.state}` : ""}</div></td>
                  <td className="p-3 text-[#E8E4DC]">{a.contact_name}<div className="text-[11px] text-[#8a857d]">{a.email}</div></td>
                  <td className="p-3 text-[#B5B0A7]">{a.requested_piece_count || "—"}</td>
                  <td className="p-3 text-[12px] uppercase tracking-[0.18em] text-[#C9A055]">{a.status}</td>
                  <td className="p-3 text-[12px] text-[#8a857d]">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <button data-testid={`pp-app-open-${a.id}`} onClick={() => setOpen(a)} className="btn-outline text-[11px]">Review</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {open && <ApplicationDrawer app={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); reload(); }} />}
    </div>
  );
}

function ApplicationDrawer({ app, onClose, onDone }) {
  const [commission, setCommission] = useState(0.10);
  const [msg, setMsg] = useState("");
  const [createOffer, setCreateOffer] = useState(true);
  const [busy, setBusy] = useState(false);
  const call = async (path, body) => {
    setBusy(true);
    try {
      await http.post(`/admin/partner/applications/${app.id}/${path}`, body);
      toast.success("Done.");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Application · ${app.business_name}`} onClose={onClose} wide testid="pp-app-modal">
      <div className="grid md:grid-cols-2 gap-4 text-[13px]">
        <Info k="Contact" v={app.contact_name} />
        <Info k="Email" v={app.email} />
        <Info k="Phone" v={app.phone || "—"} />
        <Info k="Website" v={app.website || "—"} />
        <Info k="Type" v={app.business_type || "—"} />
        <Info k="Wants" v={app.requested_piece_count || "—"} />
        <Info k="Address" v={[app.address, app.city, app.state, app.zip].filter(Boolean).join(", ")} />
        <Info k="Preferred commission" v={app.preferred_commission || "—"} />
      </div>
      {app.display_area_description && <Block label="About the space">{app.display_area_description}</Block>}
      {app.reason_for_applying && <Block label="Why they're applying">{app.reason_for_applying}</Block>}
      {app.notes && <Block label="Notes">{app.notes}</Block>}
      {app.uploaded_photos?.length > 0 && (
        <div className="mt-3">
          <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Photos</div>
          <div className="grid grid-cols-3 gap-2">{app.uploaded_photos.map((u, i) => <img key={i} src={u} alt="" className="w-full h-24 object-cover" />)}</div>
        </div>
      )}
      <div className="border-t border-white/10 mt-5 pt-5 space-y-4">
        <div className="grid sm:grid-cols-3 gap-2 items-end">
          <label className="text-[12px]">
            <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Commission %</div>
            <input type="number" step="0.01" min="0" max="1" value={commission}
              onChange={(e) => setCommission(parseFloat(e.target.value))} className="stc-input w-full" />
            <div className="text-[10px] text-[#8a857d] mt-1">0.10 = 10%</div>
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={createOffer} onChange={(e) => setCreateOffer(e.target.checked)} className="accent-[#C9A055]" />
            Also draft an Offer
          </label>
          <button disabled={busy} data-testid="pp-app-approve"
            onClick={() => call("approve", { commission_rate: commission, create_offer: createOffer })}
            className="btn-gold inline-flex items-center justify-center gap-2">
            <CheckCircle2 size={14} /> Approve
          </button>
        </div>
        <div className="grid sm:grid-cols-[1fr_auto] gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Request more info message…" className="stc-input" data-testid="pp-app-info-msg" />
          <button disabled={busy || !msg} onClick={() => call("request-info", { message: msg })}
            data-testid="pp-app-request-info" className="btn-outline inline-flex items-center gap-2">
            <MessageCircle size={14} /> Request Info
          </button>
        </div>
        <button disabled={busy} onClick={() => call("decline", { reason: msg })}
          data-testid="pp-app-decline" className="btn-outline border-red-700/40 text-red-300 w-full inline-flex items-center justify-center gap-2">
          <XCircle size={14} /> Decline
        </button>
      </div>
    </Modal>
  );
}
const Info = ({ k, v }) => (
  <div>
    <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d]">{k}</div>
    <div className="text-[#E8E4DC]">{v}</div>
  </div>
);
const Block = ({ label, children }) => (
  <div className="mt-3">
    <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>
    <div className="text-[13px] text-[#E8E4DC] whitespace-pre-wrap border border-white/10 bg-[#000] p-3">{children}</div>
  </div>
);

// ============================================================================
// PARTNERS
// ============================================================================
function Partners() {
  const [rows, reload] = useList("/admin/partner/partners");
  const [open, setOpen] = useState(null);
  if (rows === null) return <Spinner />;
  return (
    <>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr><th className="p-3 text-left">Business</th><th className="p-3 text-left">Contact</th><th className="p-3 text-left">Commission</th><th className="p-3 text-left">Assigned</th><th className="p-3 text-left">Agreement</th><th className="p-3 text-right">Action</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-[#8a857d] italic">No partners yet.</td></tr> :
              rows.map(p => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="p-3 text-[#F2EFE8]">{p.partner_business_name || "—"}</td>
                  <td className="p-3 text-[#E8E4DC]">{p.name}<div className="text-[11px] text-[#8a857d]">{p.email}</div></td>
                  <td className="p-3 text-[#C9A055]">{((p.partner_commission_rate || 0) * 100).toFixed(1)}%</td>
                  <td className="p-3 text-[#B5B0A7]">{p.assignment_count || 0}</td>
                  <td className="p-3 text-[12px]">{p.current_agreement ? "✓" : "—"}</td>
                  <td className="p-3 text-right"><button onClick={() => setOpen(p)} className="btn-outline text-[11px]" data-testid={`pp-partner-open-${p.id}`}>Details</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {open && <PartnerDrawer partner={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); reload(); }} />}
    </>
  );
}

function PartnerDrawer({ partner, onClose, onDone }) {
  const [full, setFull] = useState(null);
  useEffect(() => { http.get(`/admin/partner/partners/${partner.id}`).then(({ data }) => setFull(data)); }, [partner.id]);
  if (!full) return <Modal title="Loading…" onClose={onClose}><Spinner /></Modal>;
  return (
    <Modal title={full.partner_business_name || full.name} onClose={onClose} wide testid="pp-partner-modal">
      <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
        <Info k="Contact" v={full.name} />
        <Info k="Email" v={full.email} />
        <Info k="Phone" v={full.partner_phone || "—"} />
        <Info k="Commission rate" v={`${((full.partner_commission_rate || 0) * 100).toFixed(1)}%`} />
        <Info k="Active" v={full.partner_active ? "Yes" : "No"} />
        <Info k="Locations" v={full.locations?.length || 0} />
      </div>
      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <SubList title={`Offers (${full.offers?.length || 0})`} items={full.offers || []} render={o => `${(o.commission_rate * 100).toFixed(1)}% · ${o.status}`} />
        <SubList title={`Agreements (${full.agreements?.length || 0})`} items={full.agreements || []} render={a => `${a.status} · ${a.signed_at ? new Date(a.signed_at).toLocaleDateString() : "pending"}`} />
        <SubList title={`Assignments (${full.assignments?.length || 0})`} items={full.assignments || []} render={a => `${a.status}`} />
      </div>
      <p className="text-[11px] text-[#8a857d] mt-4">Use the dedicated tabs (Offers / Agreements / Assignments) to mutate these records.</p>
      <div className="flex justify-end mt-4"><button onClick={onDone} className="btn-outline">Close</button></div>
    </Modal>
  );
}
const SubList = ({ title, items, render }) => (
  <div className="border border-white/10 p-3">
    <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#C9A055] mb-2">{title}</div>
    {items.length === 0 ? <div className="text-[12px] text-[#8a857d] italic">—</div> : items.map((it, i) => <div key={i} className="text-[12px] text-[#E8E4DC] border-t border-white/5 py-1">{render(it)}</div>)}
  </div>
);

// ============================================================================
// OFFERS
// ============================================================================
function Offers() {
  const [rows, reload] = useList("/admin/partner/offers");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  if (rows === null) return <Spinner />;
  const sendIt = async (id) => {
    try { await http.post(`/admin/partner/offers/${id}/send`); toast.success("Offer sent."); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try { await http.delete(`/admin/partner/offers/${id}`); toast.success("Deleted."); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <>
      <button onClick={() => setCreate(true)} className="btn-gold inline-flex items-center gap-2 mb-3" data-testid="pp-offer-new"><Plus size={13} /> New Offer</button>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr><th className="p-3 text-left">Partner</th><th className="p-3 text-left">Rate</th><th className="p-3 text-left">Type</th><th className="p-3 text-left">Pieces</th><th className="p-3 text-left">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-[#8a857d] italic">No offers yet.</td></tr> :
              rows.map(o => (
                <tr key={o.id} className="border-t border-white/5" data-testid={`pp-offer-${o.id}`}>
                  <td className="p-3 text-[#F2EFE8]">{o.partner?.partner_business_name || o.partner?.name || o.partner_user_id}</td>
                  <td className="p-3 text-[#C9A055]">{(o.commission_rate * 100).toFixed(1)}%</td>
                  <td className="p-3 text-[#B5B0A7] text-[12px]">{o.commission_type}</td>
                  <td className="p-3 text-[#B5B0A7]">{o.approved_piece_count}</td>
                  <td className="p-3 text-[12px] uppercase tracking-[0.18em] text-[#C9A055]">{o.status}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEdit(o)} className="p-1.5 border border-white/10 text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055]" title="Edit"><Edit3 size={13} /></button>{" "}
                    {o.status === "draft" && <button onClick={() => sendIt(o.id)} className="p-1.5 border border-white/10 text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055]" title="Send" data-testid={`pp-offer-send-${o.id}`}><Send size={13} /></button>}{" "}
                    {o.status !== "accepted" && <button onClick={() => del(o.id)} className="p-1.5 border border-red-700/40 text-red-300" title="Delete"><Trash2 size={13} /></button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {create && <OfferEditor onClose={() => setCreate(false)} onSaved={() => { setCreate(false); reload(); }} />}
      {edit && <OfferEditor offer={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}

function OfferEditor({ offer, onClose, onSaved }) {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(() => ({
    partner_user_id: offer?.partner_user_id || "",
    commission_rate: offer?.commission_rate ?? 0.10,
    commission_type: offer?.commission_type || "percent_net",
    approved_piece_count: offer?.approved_piece_count ?? 1,
    term_length: offer?.term_length || "month_to_month",
    payout_schedule: offer?.payout_schedule || "monthly",
    payout_threshold: offer?.payout_threshold ?? 0,
    shipping_notes: offer?.shipping_notes || "",
    display_requirements: offer?.display_requirements || "",
    custom_terms: offer?.custom_terms || "",
    expires_at: offer?.expires_at || "",
  }));
  const set = (k, v) => setForm({ ...form, [k]: v });
  useEffect(() => { if (!offer) http.get("/admin/partner/partners").then(({ data }) => setPartners(data)); }, [offer]);

  const save = async () => {
    try {
      const body = { ...form };
      if (!body.expires_at) body.expires_at = null;
      if (offer) await http.patch(`/admin/partner/offers/${offer.id}`, body);
      else await http.post("/admin/partner/offers", body);
      toast.success("Saved."); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };

  return (
    <Modal title={offer ? `Edit Offer · ${offer.id}` : "New Offer"} onClose={onClose} testid="pp-offer-modal" wide>
      <div className="space-y-3">
        {!offer && (
          <Sel label="Partner *" value={form.partner_user_id} onChange={v => set("partner_user_id", v)}
            opts={[["", "Select…"], ...partners.map(p => [p.id, `${p.partner_business_name || p.name} (${p.email})`])]} />
        )}
        <div className="grid grid-cols-2 gap-3">
          <Num label="Commission rate (0.10 = 10%)" value={form.commission_rate} onChange={v => set("commission_rate", v)} step="0.01" />
          <Sel label="Commission type" value={form.commission_type} onChange={v => set("commission_type", v)}
            opts={[["percent_net", "% of Net Sale"], ["percent_gross", "% of Gross Sale"], ["flat_per_sale", "Flat per sale"], ["custom", "Custom"]]} />
          <Num label="Approved pieces" value={form.approved_piece_count} onChange={v => set("approved_piece_count", parseInt(v) || 1)} />
          <Sel label="Term" value={form.term_length} onChange={v => set("term_length", v)}
            opts={[["month_to_month", "Month-to-month"], ["3m", "3 months"], ["6m", "6 months"], ["12m", "12 months"], ["custom", "Custom"]]} />
          <Sel label="Payout schedule" value={form.payout_schedule} onChange={v => set("payout_schedule", v)}
            opts={[["monthly", "Monthly"], ["quarterly", "Quarterly"], ["custom", "Custom"]]} />
          <Num label="Min payout threshold" value={form.payout_threshold} onChange={v => set("payout_threshold", parseFloat(v) || 0)} step="0.01" />
        </div>
        <Txt label="Shipping notes" value={form.shipping_notes} onChange={v => set("shipping_notes", v)} />
        <Txt label="Display requirements" value={form.display_requirements} onChange={v => set("display_requirements", v)} />
        <Txt label="Custom terms (free text)" rows={4} value={form.custom_terms} onChange={v => set("custom_terms", v)} />
        <Field label="Expires at"><input type="date" value={(form.expires_at || "").slice(0, 10)} onChange={(e) => set("expires_at", e.target.value)} className="stc-input" /></Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={save} data-testid="pp-offer-save" className="btn-gold inline-flex items-center gap-2"><Save size={13} /> Save</button>
      </div>
    </Modal>
  );
}

// ============================================================================
// AGREEMENTS
// ============================================================================
function Agreements() {
  const [rows, reload] = useList("/admin/partner/agreements");
  const [partners, setPartners] = useState([]);
  const [view, setView] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  useEffect(() => { http.get("/admin/partner/partners").then(({ data }) => setPartners(data)); }, []);
  if (rows === null) return <Spinner />;
  const del = async (id) => {
    if (!window.confirm("Delete this pending agreement? Signed agreements cannot be deleted.")) return;
    try { await http.delete(`/admin/partner/agreements/${id}`); toast.success("Deleted."); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <>
      <button onClick={() => setSendOpen(true)} className="btn-gold inline-flex items-center gap-2 mb-3" data-testid="pp-agree-send">
        <Send size={13} /> Send New Agreement
      </button>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr><th className="p-3 text-left">Partner</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Signer</th><th className="p-3 text-left">Signed</th><th className="p-3 text-left">IP</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-[#8a857d] italic">No agreements yet.</td></tr> :
              rows.map(a => (
                <tr key={a.id} className="border-t border-white/5" data-testid={`pp-agree-${a.id}`}>
                  <td className="p-3 text-[#F2EFE8]">{a.partner?.partner_business_name || a.partner?.name || a.partner_user_id}</td>
                  <td className="p-3 text-[12px] uppercase tracking-[0.18em] text-[#C9A055]">{a.status}</td>
                  <td className="p-3 text-[#E8E4DC]">{a.signer_name || "—"}</td>
                  <td className="p-3 text-[12px] text-[#B5B0A7]">{a.signed_at ? new Date(a.signed_at).toLocaleString() : "—"}</td>
                  <td className="p-3 text-[11px] text-[#8a857d]">{a.signed_ip || "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setView(a.id)} className="p-1.5 border border-white/10 text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055] mr-1" title="View" data-testid={`pp-agree-view-${a.id}`}>
                      <Edit3 size={13} />
                    </button>
                    <a href={`${API_BASE}/api/admin/partner/agreements/${a.id}/pdf`} target="_blank" rel="noreferrer"
                      className="inline-block p-1.5 border border-white/10 text-[#8a857d] hover:border-[#C9A055] hover:text-[#C9A055] mr-1"
                      title="PDF" data-testid={`pp-agree-pdf-${a.id}`}>
                      <FileText size={13} />
                    </a>
                    {a.status !== "signed" && (
                      <button onClick={() => del(a.id)} className="p-1.5 border border-red-700/40 text-red-300" title="Delete">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {view && <AgreementViewModal aid={view} onClose={() => setView(null)} />}
      {sendOpen && <SendAgreementModal partners={partners} onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); reload(); }} />}
    </>
  );
}

function AgreementViewModal({ aid, onClose }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { http.get(`/admin/partner/agreements/${aid}`).then(({ data }) => setDoc(data)); }, [aid]);
  return (
    <Modal title={`Agreement · ${aid.slice(0, 8)}…`} onClose={onClose} wide testid="pp-agree-view-modal">
      {!doc ? <Spinner /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-[13px]">
            <Info k="Partner" v={doc.partner?.partner_business_name || doc.partner?.name} />
            <Info k="Status" v={doc.status} />
            <Info k="Signer" v={doc.signer_name || "—"} />
            <Info k="Title" v={doc.signer_title || "—"} />
            <Info k="Signed at" v={doc.signed_at ? new Date(doc.signed_at).toLocaleString() : "—"} />
            <Info k="IP" v={doc.signed_ip || "—"} />
          </div>
          <pre className="whitespace-pre-wrap font-body text-[12px] text-[#B5B0A7] max-h-[400px] overflow-y-auto border border-white/10 bg-[#000] p-3">{doc.content_rendered}</pre>
          {doc.signature_data_url && (
            <div>
              <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">Signature</div>
              <img src={doc.signature_data_url} alt="signature" className="bg-[#0a0a0a] border border-white/10 max-h-32" />
            </div>
          )}
        </div>
      )}
      <div className="mt-4 flex justify-between items-center">
        {doc && <a href={`${API_BASE}/api/admin/partner/agreements/${aid}/pdf`} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-1"><FileText size={12} /> Download PDF</a>}
        <button onClick={onClose} className="btn-outline ml-auto">Close</button>
      </div>
    </Modal>
  );
}

function SendAgreementModal({ partners, onClose, onSent }) {
  const [pid, setPid] = useState("");
  const [pieces, setPieces] = useState(1);
  const [terms, setTerms] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!pid) return toast.error("Pick a partner.");
    setBusy(true);
    try {
      await http.post("/admin/partner/agreements/send", {
        partner_user_id: pid,
        approved_piece_count: pieces,
        custom_terms: terms,
      });
      toast.success("Agreement minted + emailed to host.");
      onSent();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Send New Agreement" onClose={onClose} testid="pp-send-agree-modal">
      <p className="text-[12px] text-[#8a857d] mb-3">
        Mints a fresh agreement bound to the partner&apos;s current commission rate
        (or their accepted offer if one exists) and emails them a sign link.
      </p>
      <div className="space-y-3">
        <Sel label="Partner *" value={pid} onChange={setPid}
          opts={[["", "Select…"], ...partners.map(p => [p.id, p.partner_business_name || p.name])]} />
        <Num label="Approved pieces" value={pieces} onChange={v => setPieces(parseInt(v) || 1)} />
        <Txt label="Custom terms (optional, fills the {{admin_notes}} block)" rows={3} value={terms} onChange={setTerms} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={send} disabled={busy} className="btn-gold" data-testid="pp-send-agree-submit">
          {busy ? <Loader2 className="animate-spin" size={13} /> : "Mint & Email"}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================================
// TEMPLATES
// ============================================================================
function Templates() {
  const [rows, reload] = useList("/admin/partner/templates");
  const [edit, setEdit] = useState(null);
  const [create, setCreate] = useState(false);
  if (rows === null) return <Spinner />;
  const del = async (id) => {
    if (!window.confirm("Delete this template? Only unused templates can be removed.")) return;
    try { await http.delete(`/admin/partner/templates/${id}`); toast.success("Deleted."); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <>
      <button onClick={() => setCreate(true)} className="btn-gold inline-flex items-center gap-2 mb-3" data-testid="pp-tpl-new"><Plus size={13} /> New Template</button>
      <p className="text-[11px] text-[#8a857d] mb-3">Editing a template&apos;s content creates a new <b>version</b>. Signed agreements remain locked to the historical version they were signed under.</p>
      <div className="space-y-2">
        {rows.map(t => (
          <div key={t.id} className="border border-white/10 bg-[#0a0a0a] p-4 flex items-center gap-3 flex-wrap" data-testid={`pp-tpl-${t.id}`}>
            <div className="flex-1">
              <div className="font-display text-lg text-[#F4F1EA]">{t.name}</div>
              <div className="text-[11px] text-[#8a857d]">v{t.version} · {t.type} · {t.active ? <span className="text-emerald-300">active</span> : <span className="text-[#8a857d]">archived</span>}</div>
            </div>
            <button onClick={() => setEdit(t)} className="btn-outline text-[11px]"><Edit3 size={12} /> Edit</button>
            <button onClick={() => del(t.id)} className="p-1.5 border border-red-700/40 text-red-300"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>
      {create && <TemplateEditor onClose={() => setCreate(false)} onSaved={() => { setCreate(false); reload(); }} />}
      {edit && <TemplateEditor tpl={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </>
  );
}
function TemplateEditor({ tpl, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: tpl?.name || "", type: tpl?.type || "host_agreement",
    content: tpl?.content || "", active: tpl?.active ?? true,
  });
  const save = async () => {
    try {
      if (tpl) await http.patch(`/admin/partner/templates/${tpl.id}`, form);
      else await http.post("/admin/partner/templates", form);
      toast.success("Saved."); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed."); }
  };
  return (
    <Modal title={tpl ? `Edit Template · ${tpl.name}` : "New Template"} onClose={onClose} wide testid="pp-tpl-modal">
      <div className="space-y-3">
        <Txt label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} />
        <Sel label="Type" value={form.type} onChange={v => setForm({ ...form, type: v })}
          opts={[["host_agreement", "Host Agreement"], ["commission_terms", "Commission Terms"], ["display_terms", "Artwork Display Terms"], ["privacy_policy", "Privacy Policy"], ["host_faq", "Host FAQ"], ["payout_policy", "Payout Policy"], ["termination_policy", "Termination Policy"], ["manual_sale_policy", "Manual Sale Policy"]]} />
        <Field label="Content (supports {{host_name}}, {{business_name}}, {{commission_rate}}, {{commission_type}}, {{payout_schedule}}, {{term_length}}, {{approved_piece_count}}, {{agreement_date}}, {{admin_notes}})">
          <textarea rows={16} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
            className="stc-input w-full font-mono text-[12px]" data-testid="pp-tpl-content" />
        </Field>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#C9A055]" />
          Active (used for new agreements). Activating deactivates other templates of the same type.
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={save} className="btn-gold" data-testid="pp-tpl-save">Save</button>
      </div>
    </Modal>
  );
}

// ============================================================================
// ASSIGNMENTS
// ============================================================================
function Assignments() {
  const [rows, reload] = useList("/admin/partner/assignments");
  const [show, setShow] = useState(false);
  const [partners, setPartners] = useState([]);
  const [art, setArt] = useState([]);
  useEffect(() => {
    http.get("/admin/partner/partners").then(({ data }) => setPartners(data));
    http.get("/artworks").then(({ data }) => setArt(data || []));
  }, []);
  if (rows === null) return <Spinner />;
  const del = async (id) => {
    if (!window.confirm("Remove this assignment?")) return;
    await http.delete(`/admin/partner/assignments/${id}`); reload();
  };
  return (
    <>
      <button onClick={() => setShow(!show)} className="btn-gold inline-flex items-center gap-2 mb-3" data-testid="pp-assign-new"><Plus size={13} /> Assign Artwork</button>
      {show && <AssignmentForm partners={partners} artworks={art} onSaved={() => { setShow(false); reload(); }} onCancel={() => setShow(false)} />}
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr><th className="p-3 text-left">Partner</th><th className="p-3 text-left">Artwork</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Assigned</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-[#8a857d] italic">No assignments yet.</td></tr> :
              rows.map(a => (
                <tr key={a.id} className="border-t border-white/5" data-testid={`pp-assign-${a.id}`}>
                  <td className="p-3 text-[#F2EFE8]">{partners.find(p => p.id === a.partner_user_id)?.partner_business_name || a.partner_user_id}</td>
                  <td className="p-3 text-[#E8E4DC]">{a.artwork?.title}</td>
                  <td className="p-3 text-[12px]">
                    <select value={a.status} onChange={async (e) => { await http.patch(`/admin/partner/assignments/${a.id}`, { status: e.target.value }); reload(); }}
                      data-testid={`pp-assign-status-${a.id}`}
                      className="stc-input text-[12px] py-1">
                      {["assigned", "shipped", "delivered", "installed", "displayed", "sold_locally", "sold_online", "damaged", "missing", "returned", "replaced", "archived"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-[12px] text-[#8a857d]">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right"><button onClick={() => del(a.id)} className="p-1.5 border border-red-700/40 text-red-300"><Trash2 size={13} /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
function AssignmentForm({ partners, artworks, onSaved, onCancel }) {
  const [form, setForm] = useState({ partner_user_id: "", artwork_id: "", notes: "" });
  const submit = async () => {
    if (!form.partner_user_id || !form.artwork_id) return toast.error("Pick partner + artwork.");
    try { await http.post("/admin/partner/assignments", form); toast.success("Assigned."); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-4 mb-3 space-y-2">
      <Sel label="Partner" value={form.partner_user_id} onChange={v => setForm({ ...form, partner_user_id: v })}
        opts={[["", "Select…"], ...partners.map(p => [p.id, p.partner_business_name || p.name])]} />
      <Sel label="Artwork" value={form.artwork_id} onChange={v => setForm({ ...form, artwork_id: v })}
        opts={[["", "Select…"], ...artworks.map(a => [a.id, a.title])]} />
      <Txt label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-outline">Cancel</button>
        <button onClick={submit} className="btn-gold" data-testid="pp-assign-save">Assign</button>
      </div>
    </div>
  );
}

// ============================================================================
// REQUESTS
// ============================================================================
function Requests() { return <SimpleAdminList url="/admin/partner/requests" patchUrl={(id) => `/admin/partner/requests/${id}`} cols={[["Type", "request_type"], ["Qty", "quantity"], ["Reason", "reason"], ["Status", "status"]]} title="Requests" />; }
function Tickets()  { return <SimpleAdminList url="/admin/partner/tickets"  patchUrl={(id) => `/admin/partner/tickets/${id}`}  cols={[["Subject", "subject"], ["Category", "category"], ["Status", "status"]]} title="Tickets" />; }
function ManualSales() {
  const [rows, reload] = useList("/admin/partner/sales");
  if (rows === null) return <Spinner />;
  const patch = async (id, body) => { await http.patch(`/admin/partner/sales/${id}`, body); reload(); };
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
          <tr><th className="p-3 text-left">Partner</th><th className="p-3 text-left">Amount</th><th className="p-3 text-left">Commission</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Buyer</th><th className="p-3 text-right">Actions</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-[#8a857d] italic">No manual sales yet.</td></tr> :
            rows.map(s => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="p-3 text-[#F2EFE8]">{s.partner?.partner_business_name || s.partner?.name}</td>
                <td className="p-3 text-[#F2EFE8]">${parseFloat(s.sale_amount).toFixed(2)}</td>
                <td className="p-3 text-[#C9A055]">${parseFloat(s.commission_amount || 0).toFixed(2)}</td>
                <td className="p-3 text-[12px] uppercase">{s.status}</td>
                <td className="p-3 text-[#B5B0A7]">{s.buyer_name || "—"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => patch(s.id, { status: "approved" })} className="text-emerald-300 px-2" data-testid={`pp-sale-approve-${s.id}`}>✓</button>
                  <button onClick={() => patch(s.id, { status: "denied" })} className="text-red-300 px-2" data-testid={`pp-sale-deny-${s.id}`}>✗</button>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleAdminList({ url, patchUrl, cols, title }) {
  const [rows, reload] = useList(url);
  if (rows === null) return <Spinner />;
  const setStatus = async (id, status) => { await http.patch(patchUrl(id), { status }); reload(); };
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
          <tr>
            <th className="p-3 text-left">Partner</th>
            {cols.map(c => <th key={c[1]} className="p-3 text-left">{c[0]}</th>)}
            <th className="p-3 text-right">Update Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={cols.length + 2} className="p-8 text-center text-[#8a857d] italic">No {title.toLowerCase()} yet.</td></tr> :
            rows.map(r => (
              <tr key={r.id} className="border-t border-white/5">
                <td className="p-3 text-[#F2EFE8]">{r.partner?.partner_business_name || r.partner?.name}</td>
                {cols.map(c => <td key={c[1]} className="p-3 text-[#E8E4DC]">{typeof r[c[1]] === "string" ? r[c[1]] : JSON.stringify(r[c[1]])}</td>)}
                <td className="p-3 text-right">
                  <select value={r.status || ""} onChange={(e) => setStatus(r.id, e.target.value)}
                    className="stc-input text-[12px] py-1" data-testid={`pp-${title.toLowerCase()}-status-${r.id}`}>
                    {["open", "in_progress", "approved", "denied", "fulfilled", "resolved", "closed"].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// COMMISSIONS ADMIN
// ============================================================================
function CommissionsAdmin() {
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({ partner_user_id: "", status: "", month: "" });
  const [rows, setRows] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const { data } = await http.get(`/admin/partner/commissions?${params}`);
    setRows(data);
  };
  useEffect(() => { http.get("/admin/partner/partners").then(({ data }) => setPartners(data)); }, []);
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const csvUrl = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    return `${API_BASE}/api/admin/partner/commissions/export.csv?${params}`;
  };

  const total = (rows || []).reduce((s, r) => s + (r.commission_amount || 0), 0);

  return (
    <div className="space-y-4" data-testid="pp-commissions">
      <div className="grid sm:grid-cols-4 gap-2 items-end">
        <Sel label="Partner" value={filters.partner_user_id} onChange={v => setFilters({ ...filters, partner_user_id: v })}
          opts={[["", "All partners"], ...partners.map(p => [p.id, p.partner_business_name || p.name])]} />
        <Sel label="Status" value={filters.status} onChange={v => setFilters({ ...filters, status: v })}
          opts={[["", "All"], ["pending", "Pending"], ["approved", "Approved"], ["denied", "Denied"], ["paid", "Paid"]]} />
        <Field label="Month (YYYY-MM)">
          <input value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            placeholder="2026-06" className="stc-input w-full" data-testid="pp-comm-month" />
        </Field>
        <div className="flex gap-2">
          <button onClick={() => setAdjustOpen(true)} className="btn-outline inline-flex items-center gap-1"><Plus size={12} /> Adjust</button>
          <a href={csvUrl()} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-1" data-testid="pp-comm-csv">
            <FileText size={12} /> CSV
          </a>
          <button onClick={() => setReportOpen(true)} className="btn-gold inline-flex items-center gap-1" data-testid="pp-comm-report">
            <Send size={12} /> Send Report
          </button>
        </div>
      </div>

      <div className="border border-[#C9A055]/30 bg-[#0a0a0a] p-4 flex items-center gap-6">
        <div>
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">Filtered Total</div>
          <div className="font-display text-3xl text-[#C9A055]">${total.toFixed(2)}</div>
        </div>
        <div className="text-[12px] text-[#8a857d]">{rows?.length || 0} rows</div>
      </div>

      {rows === null ? <Spinner /> : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
              <tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Partner</th><th className="p-3 text-left">Source</th><th className="p-3 text-left">Sale</th><th className="p-3 text-left">Rate</th><th className="p-3 text-left">Commission</th><th className="p-3 text-left">Status</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-[#8a857d] italic">No commission rows match.</td></tr> :
                rows.map((r, i) => (
                  <tr key={`${r.id}-${i}`} className="border-t border-white/5">
                    <td className="p-3 text-[12px] text-[#8a857d]">{r.date ? new Date(r.date).toLocaleDateString() : "—"}</td>
                    <td className="p-3 text-[#F2EFE8]">{r.partner?.partner_business_name || r.partner?.name || r.partner_user_id}</td>
                    <td className="p-3 text-[12px] uppercase tracking-[0.18em] text-[#B5B0A7]">{r.source}</td>
                    <td className="p-3 text-[#E8E4DC]">${(r.sale_amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-[#B5B0A7]">{((r.commission_rate || 0) * 100).toFixed(1)}%</td>
                    <td className="p-3 text-[#C9A055]">${(r.commission_amount || 0).toFixed(2)}</td>
                    <td className="p-3 text-[12px] uppercase">{r.status || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {adjustOpen && <AdjustmentModal partners={partners} onClose={() => setAdjustOpen(false)} onSaved={() => { setAdjustOpen(false); load(); }} />}
      {reportOpen && <MonthlyReportModal partners={partners} onClose={() => setReportOpen(false)} />}
    </div>
  );
}

function AdjustmentModal({ partners, onClose, onSaved }) {
  const [form, setForm] = useState({ partner_user_id: "", amount: "", notes: "" });
  const save = async () => {
    if (!form.partner_user_id) return toast.error("Pick a partner.");
    try {
      await http.post("/admin/partner/commissions/adjust", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Adjustment saved."); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <Modal title="Add Commission Adjustment" onClose={onClose} testid="pp-adjust-modal">
      <div className="space-y-3">
        <Sel label="Partner *" value={form.partner_user_id} onChange={v => setForm({ ...form, partner_user_id: v })}
          opts={[["", "Select…"], ...partners.map(p => [p.id, p.partner_business_name || p.name])]} />
        <Num label="Amount (negative for clawback)" value={form.amount} onChange={v => setForm({ ...form, amount: v })} step="0.01" />
        <Txt label="Notes" rows={3} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={save} className="btn-gold" data-testid="pp-adjust-save">Save</button>
      </div>
    </Modal>
  );
}

function MonthlyReportModal({ partners, onClose }) {
  const [pid, setPid] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!pid) return toast.error("Pick a partner.");
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/partner/monthly-report/${pid}/send?month=${month}`);
      toast.success(`Sent. ${data.report.row_count} rows · $${data.report.commission_total.toFixed(2)} commission.`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Send Monthly Report" onClose={onClose} testid="pp-report-modal">
      <p className="text-[12px] text-[#8a857d] mb-3">Emails a snapshot (scans, sales, commission, top piece) to the chosen partner for the given month.</p>
      <div className="space-y-3">
        <Sel label="Partner *" value={pid} onChange={setPid}
          opts={[["", "Select…"], ...partners.map(p => [p.id, p.partner_business_name || p.name])]} />
        <Field label="Month (YYYY-MM)">
          <input value={month} onChange={(e) => setMonth(e.target.value)} className="stc-input w-full" />
        </Field>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={send} disabled={busy} className="btn-gold" data-testid="pp-report-send">{busy ? <Loader2 className="animate-spin" size={13} /> : "Send Report"}</button>
      </div>
    </Modal>
  );
}

// ============================================================================
// PAYOUTS ADMIN
// ============================================================================
function PayoutsAdmin() {
  const [rows, reload] = useList("/admin/partner/payouts");
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { http.get("/admin/partner/partners").then(({ data }) => setPartners(data)); }, []);
  if (rows === null) return <Spinner />;
  const del = async (id) => {
    if (!window.confirm("Delete this payout record?")) return;
    try { await http.delete(`/admin/partner/payouts/${id}`); toast.success("Deleted."); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="space-y-3" data-testid="pp-payouts">
      <button onClick={() => setOpen(true)} className="btn-gold inline-flex items-center gap-2" data-testid="pp-payout-new"><Plus size={13} /> Send Payout</button>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-[#8a857d] font-ui text-[9px] tracking-[0.22em] uppercase">
            <tr><th className="p-3 text-left">Partner</th><th className="p-3 text-left">Amount</th><th className="p-3 text-left">Method</th><th className="p-3 text-left">Reference</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Paid</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-[#8a857d] italic">No payouts yet.</td></tr> :
              rows.map(p => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="p-3 text-[#F2EFE8]">{p.partner?.partner_business_name || p.partner?.name || p.partner_user_id}</td>
                  <td className="p-3 text-[#C9A055]">${(p.amount || p.amount_paid || 0).toFixed(2)}</td>
                  <td className="p-3 text-[12px] uppercase">{p.payment_method || "—"}</td>
                  <td className="p-3 text-[12px] text-[#B5B0A7] font-mono">{p.payment_reference || "—"}</td>
                  <td className="p-3 text-[12px] uppercase">{p.status || "—"}</td>
                  <td className="p-3 text-[12px] text-[#8a857d]">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => del(p.id)} className="p-1.5 border border-red-700/40 text-red-300" data-testid={`pp-payout-del-${p.id}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {open && <PayoutModal partners={partners} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); reload(); }} />}
    </div>
  );
}

function PayoutModal({ partners, onClose, onSaved }) {
  const [form, setForm] = useState({
    partner_user_id: "", amount: "", payment_method: "venmo",
    payment_reference: "", notes: "", period_start: "", period_end: "",
  });
  const save = async () => {
    if (!form.partner_user_id) return toast.error("Pick a partner.");
    try {
      await http.post("/admin/partner/payouts", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Payout sent."); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed."); }
  };
  return (
    <Modal title="Send Payout" onClose={onClose} wide testid="pp-payout-modal">
      <div className="space-y-3">
        <Sel label="Partner *" value={form.partner_user_id} onChange={v => setForm({ ...form, partner_user_id: v })}
          opts={[["", "Select…"], ...partners.map(p => [p.id, p.partner_business_name || p.name])]} />
        <div className="grid grid-cols-2 gap-3">
          <Num label="Amount $ *" value={form.amount} onChange={v => setForm({ ...form, amount: v })} step="0.01" />
          <Sel label="Method" value={form.payment_method} onChange={v => setForm({ ...form, payment_method: v })}
            opts={[["venmo", "Venmo"], ["cashapp", "Cash App"], ["paypal", "PayPal"], ["check", "Check"], ["other", "Other"]]} />
        </div>
        <Txt label="Reference (txn ID, check #, etc)" value={form.payment_reference} onChange={v => setForm({ ...form, payment_reference: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Period start"><input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} className="stc-input w-full" /></Field>
          <Field label="Period end"><input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} className="stc-input w-full" /></Field>
        </div>
        <Txt label="Notes" rows={3} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
        <p className="text-[11px] text-[#8a857d]">Saving emails the host a confirmation with the amount and reference.</p>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="btn-outline">Cancel</button>
        <button onClick={save} className="btn-gold" data-testid="pp-payout-save">Send Payout</button>
      </div>
    </Modal>
  );
}

// shared inputs
function Field({ label, children }) {
  return (<label className="block">
    <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mb-1">{label}</div>{children}
  </label>);
}
function Sel({ label, value, onChange, opts }) {
  return <Field label={label}>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="stc-input w-full">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </Field>;
}
function Num({ label, value, onChange, step = "1" }) {
  return <Field label={label}>
    <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className="stc-input w-full" />
  </Field>;
}
function Txt({ label, value, onChange, rows }) {
  return <Field label={label}>
    {rows ? <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="stc-input w-full font-body" /> :
      <input value={value} onChange={(e) => onChange(e.target.value)} className="stc-input w-full" />}
  </Field>;
}
